import PropTypes from 'prop-types';

import StagesController from './StageController.js';

import WithType from './../WithType';

import map from '../../../../config/main.config.js';

import nodes from '../../../../nodes.js';

import locations from '../../../../locations.js'

import {aStar, euclidianDistance, getLargestStraightSegment} from '../helpers/aStar.js';

const {
    map: {
        domain, uri_images, has_selection, final_marker_plus_bottom, show_map_tool,
        pixels_by_meter, should_be_fixed_route, should_be_fixed_start, final_marker_icon_direction,
        qrcode_origin_url, qrcode_size, qrcode_top, qrcode_right, show_distance,
        dash_segments, line_color, line_thickness, map_layers, select_groups
    }
} = map;

let _this = {};

/**
 * Handles user interaction with the map. Start point and goal can be selected by clicking stage or using HTML selects, both can be used in parallel.
 * This class also handles Qrcode generation, Maps layers and different map modes:
 * - Free one to many -> Any location can be traced to any other location.
 * - Fixed start to many -> Initial location is fixed and can be traced to any other location; related function: setFixedStartPoint().
 * - Fixed start and fixed end -> Single route - can't be changed, used in mobile mode. related function: setFixedRoute().
 * 
 * 'Maps layers' are adobe animate ocurrences to show temporarily, these highlight map locations and are to be shown/hide on user interaction
 * to route selecting or layers selection.
 */
class StageMapSelector extends StagesController {
    constructor(props, main) {
        super(props);
        this.props = props;
        this.main = main;
        this.$qrcode = null;

        const frame_document = this.getFrameDocument();
        this.map_canvas = frame_document.querySelector('canvas');

        const current_frame = this.getFrame();
        this.occ_current = current_frame.occ_sprite;

        this.occ_distance = current_frame.occ_coordinate.occ_distance;
        this.occ_path_indicator = current_frame.occ_path_indicator;
        this.occ_select_indicator = current_frame.occ_select_indicator;

        this.start_select = {};
        this.final_select = {};
        // Select elements to interact with map by selection.
        // this.start_select_el = document.getElementById('start_select');
        // this.final_select_el = document.getElementById('final_select');

        // Icons for current and goal locations.
        this.start_icon = this.occ_current?.marker_start;
        this.final_icon = this.occ_current?.marker_final;
        // Opens search modal.
        this.search_btn = this.occ_current?.btn_search;
        this.fixed_route; // Flag for fixed (static) route - mobile mode.
        this.fixed_start; // Flag for fixed starting point.

        this.state = {
            map_layer_active: false, // Flag for identifying if there is a map layer currently active.
            start: null,
            end: null,
        };

        this.listeners = {
            traced_line_tick: null // Ticker for animated dashed line.
        };

        _this = this;
    };

    /** Returns this frame canvas; may not be the main canvas if this is a multi page app. */
    getFrameDocument = () => {
        const iframe = document.querySelector('#load_iframe');
        return iframe.contentDocument || iframe.contentWindow.document;
    }

    /** Init functions below determine map mode to be set and Qrcode. Related values must be selected on main.js. */
    initMapSelector = () => {
        this.createMapElements()
        this.setFixedRoute();
        if (this.fixed_route) return;
        this.initQrCode();
        this.setFixedStartPoint();
        this.initModal();
        this.handleCanvas();
        this.handleSelection();
        this.handleClearPathButton();
        this.handleSubtitle();
    };

    /** Create HTML elements needed for map selection. */
    createMapElements = () => {
        const
            this_document = this.getFrameDocument(),
            start_label = document.createElement('label'),
            start_select = document.createElement('select'),
            final_label = document.createElement('label'),
            final_select = document.createElement('select');

        // Start.
        start_label.id = 'label_origin';
        start_label.htmlFor = 'start_select';
        start_select.id = 'start_select';
        start_select.name = 'start_select';
        start_select.style.display = 'none';
        // End.
        final_label.id = 'label_final';
        final_label.htmlFor = 'final_select';
        final_select.id = 'final_select';
        final_select.name = 'final_select';
        final_select.style.display = 'none';
        // Set at state.
        this.start_select = start_select;
        this.final_select = final_select;

        this_document.body.append(start_label, start_select, final_label, final_select);
    }

    /** Disable the destination select. */
    disableFinalSelect = () => {
        this.final_select.setAttributeNode(document.createAttribute('disabled'));
    };

    /** Set Qrcode image to default template image. */
    setQrcodeTemplate = () => {
        if (this.$qrcode) {
            document.querySelector('#qrcode > img').src = `${domain}/${uri_images}/qrcode-template.svg`;
        }
    };

    /**
     * Set the marker position coordinates.
     * @param {createjs.DisplayObject} marker Symbol on stage that is a start or goal marker.
     * @param {String} x Marker coordinate.
     * @param {String} y Marker coordinate.
     */
    setMarkerToPosition = (marker, x, y) => {
        x = parseFloat(x);
        y = parseFloat(y);
        marker.x = x;
        marker.y = (marker.name === 'marker_final') ? (y + final_marker_plus_bottom) : y;
    };

    /**
     * Set selected location marker, set location id at start_select and enables final_select.
     * @param {{id: String, x: String, y: String}} location_node Node to be set.
     * @param {createjs.DisplayObject} icon Symbol on stage that is a start or goal marker.
     */
    initStartLocation = (location_node, icon) => {
        const
            {id, x, y} = location_node,
            {final_select} = this;

        this.setState({...this.state, start: id});
        this.setMarkerToPosition(icon, x, y);

        if (!this.start_select) {
            return;
        }

        // Remove event so there's no overhead if user is selecting a route before route is drawn completly.
        //console.log('ticke-remove-test',this.listeners)
        // if (this.listeners.traced_line_tick) {
            createjs.Ticker.off('tick', this.listeners.traced_line_tick);
            this.listeners.traced_line_tick = null;
        // }

        this.start_select.value = id;

        if (final_select.hasAttribute('disabled')) {
            final_select.removeAttribute('disabled');
        }
        this.setQrcodeTemplate();
    };

    /**
     * Init final location and call drawPath.
     * @param {{id: String, x: String, y: String}} location_node 
     * @param {createjs.DisplayObject} icon Symbol on stage that is a start or goal marker.
     * @returns 
     */
    initFinalLocation = (location_node, icon) => {
        const
            {fixed_start} = this,
            {id, x, y} = location_node,
            {start} = this.state;

        if (icon) { // If location is at another floor, don't put marker at the node because the path has not finished at current floor.
            this.setMarkerToPosition(icon, x, y);
        }

        if (!this.final_select) {
            return;
        }

        // Don't allow to select start AND end position at the same node.
        if (this.state.start === id) {
            return;
        }

        this.final_select.value = id;
        this.setState({...this.state, end: id});

        const
            start_data = nodes.find(node => node.id === start),
            goal_data = nodes.find(node => node.id === id),
            goal_location = locations.find(location => location.node === id);

        // Set which direction the destination icon should face;
        const goal_node_direction = parseFloat(goal_data.x) >= parseFloat(start_data.x) ? 'right' : 'left';

        // this.occ_path_indicator.visible = true;
        // this.occ_preview_destiny.text = goal_location.id;

        if (final_marker_icon_direction === goal_node_direction) {
            icon.scaleX = -1;
        } else {
            icon.scaleX = 1;
        }

        if (!fixed_start) { // Disable select if start position is not fixed.
            this.disableFinalSelect();
        }
    };

    /**
     * Init Qrcode object and set template svg.
     * @returns If there's no Qrcode.
     */
    initQrCode = () => {
        // If this app has a qrcode, init it.
        if (!qrcode_origin_url || !qrcode_origin_url.trim()) return;
        if (!QRCode) {
            console.warn('Missing Qrcodejs.');
        }

        const qrcode_el = document.getElementById('qrcode');

        // Instantiate qrcode, show template image after rendering.
        this.$qrcode = new QRCode(qrcode_el, {width: qrcode_size, height: qrcode_size});

        qrcode_el.visibility = 'visible';
        // Set absolute Qrcode position.
        qrcode_el.style.top = qrcode_top;
        qrcode_el.style.right = qrcode_right;
        // Set qrcode img properties.
        const img = document.querySelector('#qrcode > img');
        img.style.display = 'initial';
        img.style.visibility = 'visible';
        img.style.width = `${qrcode_size}px`;
        img.style.height = `${qrcode_size}px`;
        img.style.position = 'absolute';
        img.draggable = false;
        img.src = `${domain}/${uri_images}/qrcode-template.svg`;
        // createjs lib creates a div with padding on any canvas tag, so remove it to align the template img.
        document.querySelector('#qrcode > canvas').style.display = 'none';
    };

    /**
     * Generates the QRCode containing an URL with the necessary parameters for loading
     * the map with currently selected path/layer. When a map layer is showing, no path should be traced.
     * @param {createjs.DisplayObject} layer layer currently selected to show.
     * @param {String} layer Must match an existing map layer occurence name.
     * @returns {undefined} early return.
     */
    makePathQRCode = (layer=null) => {
        const {start, end} = this.state;

        // If qrcode element exists, make it visible and set the URL with the params to show the selected location.
        if (!this.$qrcode) return;

        let url;

        if (layer) {
            url = `${qrcode_origin_url}?layer=${layer}`;
        } else if (start && end) {
            url = `${qrcode_origin_url}?start=${start}&end=${end}`;
        } else {
            console.warn('No start/end values nor layer param to create Qrcode.');
            return;
        }

        this.$qrcode.makeCode(url);
    };

    /**
     * Draw the path and animate it on two phases: 1. trace location from start to end node; 2. Turn path into dashed lines.
     * @param {createjs.DisplayObject} occ Layer to day the path.
     * @param {[{id:String, x:Number, y:Number, edges:path}]} path Path with coordinates to be drawn.
     * @param {Object} opts Options to change path (color, segments size, duration).
     * @returns Early null return if there's no path found.
     */
    animatePathFromTo = (occ, path, opts = {}) => {
        if (!path || path.length < 2) {
            show_map_tool && console.warn('No path found');
            return;
        }

        const
            line_color = opts.line_color ?? '#FF432E',
            line_dash_speed = opts.line_dash_speed ?? 50,
            line_thickness = opts.line_thickness ?? 5,
            time_per_point = opts.time_per_point ?? 150,
            duration = time_per_point * (path.length);

        function segLen(a, b) {
            const dx = b.x - a.x, dy = b.y - a.y;
            return Math.hypot(dx, dy);
        }

        const segments = [];
        let total = 0;
        for (let i = 0; i < path.length - 1; i++) {
            const len = segLen(path[i], path[i+1]);
            segments.push({from: path[i], to: path[i+1], len, start: total});
            total += len;
        }

        const path_shape = new createjs.Shape(); // where the visible line is drawn.
        occ.addChild(path_shape);

        /** Draw line at 'time'. */
        function drawAt(t) {
            const dist = total * t;

            // Find current segment.
            let seg = segments[0];
            for (let i = 0; i < segments.length; i++) {
                if (dist <= segments[i].start + segments[i].len || i === segments.length - 1) {
                    seg = segments[i];
                    break;
                }
            }

            const
                dist_on_seg = Math.max(0, Math.min(seg.len, dist - seg.start)),
                norm_distance = seg.len ? dist_on_seg / seg.len : 1, // normalized distance (number between 0 and 1).
                // x and y coords to current segment.
                seg_x = seg.from.x + (seg.to.x - seg.from.x) * norm_distance,
                seg_y = seg.from.y + (seg.to.y - seg.from.y) * norm_distance;

            // Redraw the partial path up to (seg_x, seg_y).
            const path_shape_graphics = path_shape.graphics;
            path_shape_graphics.clear()
                .setStrokeStyle(line_thickness, 'round', 'round')
                .beginStroke(line_color)
                .moveTo(path[0].x, path[0].y);

            // Full segments.
            for (let i = 0; i < segments.indexOf(seg); i++) {
                path_shape_graphics.lineTo(segments[i].to.x, segments[i].to.y);
            }
            // Partial final segment.
            path_shape_graphics.lineTo(seg_x, seg_y).endStroke();
        }

        // Animate draw line.
        let started = null;
        function handleDrawLineTick(event) {
            if (started === null) started = event.runTime;

            const
                elapsed = event.runTime - started,
                time = Math.min(1, elapsed / duration);

            drawAt(time);

            if (time === 1) {
                // After path is traced, draw line as animated dashed line.
                const pattern_len = dash_segments[0] + dash_segments[1];
                let dash_offset = 0;

                function handleDashedLineTick(event) {
                    // e.delta is ms since last tick — smoother & frame-rate independent.
                    const dt = event.delta / 7000; // seconds since last frame.

                    // Advance, then wrap to keep a steady, repeating rhythm.
                    dash_offset = (dash_offset - line_dash_speed * dt) % pattern_len;
                    // keep it in [-pattern_len, 0) or [0, pattern_len) — either is fine.
                    if (dash_offset < -pattern_len) dash_offset += pattern_len;
                    if (dash_offset >  pattern_len) dash_offset -= pattern_len;

                    const path_shape_graphics = path_shape.graphics;
                    path_shape_graphics.clear()
                        .setStrokeStyle(line_thickness, 'round', 'round')
                        .beginStroke(line_color)
                        .setStrokeDash(dash_segments, dash_offset)
                        .moveTo(path[0].x, path[0].y);

                    path.forEach(pos => {
                        path_shape_graphics.lineTo(pos.x, pos.y);
                    });

                    path_shape_graphics.endStroke();
                }

                // Adds ticker event for animated dashed line.
                if (_this.listeners.traced_line_tick) {
                    createjs.Ticker.removeEventListener('tick', _this.listeners.traced_line_tick);
                    _this.listeners.traced_line_tick = null;
                }
                _this.listeners.traced_line_tick = createjs.Ticker.on('tick', handleDashedLineTick);

                createjs.Ticker.removeEventListener('tick', handleDrawLineTick); // Removes event drawing straight lines for line path.
            }
        }
        createjs.Ticker.addEventListener('tick', handleDrawLineTick);
    };

    /**
     * Get the shortest path between start and goal node, then draw it on stage. Indicate approximate distance to be traveled.
     * @param {Object} nodes Nodes to be searched.
     * @param {String} start_id Initial node id.
     * @param {String} goal_id Final node id.
     */
    drawPath = (nodes, start_id, goal_id) => {
        const
            result = aStar(nodes, start_id, goal_id),
            {segment, direction} = getLargestStraightSegment(result),
            {x, y} = segment[0],
            {path, score} = result,
            aprox_distance = this.parseToFloat(score/pixels_by_meter);

        const
            p1 = segment[0],
            p2 = segment[segment.length - 1],
            mid_x = (parseFloat(p1.x) + parseFloat(p2.x)) / 2,
            mid_y = (parseFloat(p1.y) + parseFloat(p2.y)) / 2;

        this.occ_distance.visible = true;
        this.occ_distance.x = mid_x;
        this.occ_distance.y = mid_y;
        this.occ_distance.distance_text.text = `Distância aprox.: ${aprox_distance} metros.`;

        if (!result?.path && !result?.score) return;

        this.animatePathFromTo(
            this.occ_current,
            path,
            {
                line_color,
                line_thickness,
                time_per_point: 50 // Time taken per node (in ms) to reach destiny.
            }
        );

        // Set approximate distance in order to reach path at dynamic text display.
        //console.log('Pixels per meter', pixels_by_meter, 'px dist', score, 'aprox dist:', score/pixels_by_meter);
        // const {occ_distance} = this.occ_current;
        const {occ_preview_distance} = this.occ_path_indicator;
        if (occ_preview_distance && show_distance) {
            // occ_preview_distance.visible = true;
            this.occ_path_indicator.visible = true;
            occ_preview_distance.text = `Distância aproximada:\n${aprox_distance} metros.`;
        }
    };

    /**
     * If both positions are set, draw path.
     * @param {Function} callback function to call after path is drawn.
     * @returns {undefined} for early return;
     */
    shouldDrawPath = (callback=null) => {
        const {start, end} = this.state;

        if (!start || !end) return;

        this.drawPath(nodes, start, end);
        (qrcode_origin_url && qrcode_origin_url.trim()) && this.makePathQRCode();
        (typeof callback === 'function') && callback();
    };

    /** Resets the start and final markers to the top of the screen, out of the canvas view so user can't see it. */
    resetMarkersPositions = () => {
        const {fixed_start} = this;
        // Reset starting position only if its not fixed.
        if (!fixed_start) {
            this.setMarkerToPosition(this.start_icon, 30, -60);
        }
        this.setMarkerToPosition(this.final_icon, 30, -60);
    };

    /** Iterate map layers making them invisible. */
    hideMapLayers = () => {
        if (!this.state.map_layer_active) return;

        map_layers.forEach(layer => {
            this.occ_current[`layer_${layer}`].visible = false;
        });

        this.state.map_layer_active = false;
    };

    /** Return true if start and end positions are already set. Make map layers invisible. */
    shouldResetState = () => {
        const {start, end} = this.state;

        this.hideMapLayers();
        if (this.occ_path_indicator.visible && show_distance) this.occ_path_indicator.visible = false; // Hide distance preview.

        return start && end;
    };

    resetState = (is_clear_button = false) => {
        const {occ_current, fixed_start, final_select} = this;

        this.occ_distance.visible = false;
        // Reset canvas.
        occ_current.removeAllChildren(); // Path is the only thing drawn on occ box.
        this.setState({...this.state, end: null});
        this.resetMarkersPositions();
        // if (occ_distance && show_distance) this.distance.visible = false; // Hide distance preview.

        // Removes tick event related to animated dashed line.
        createjs.Ticker.removeEventListener('tick', this.listeners.traced_line_tick);
        this.listeners.traced_line_tick = null;

        // this.occ_path_indicator.visible = false;

        // If start position is not fixed or resetState was called from clear path button, reset select values.
        if (!fixed_start) {
            this.setState({...this.state, start: null});
            final_select.value = '';
        }
        if (fixed_start && is_clear_button) {
            final_select.value = '';
            this.hideMapLayers();
        }
    };

    /** Manage canvas 'click' interactions. */
    handleCanvas = () => {
        function handleClick (event) {
            // const clicked_point = stage.globalToLocal(event.clientX, event.clientY);
            const clicked_point = _this.occ_current.globalToLocal(event.clientX, event.clientY);

            const clicked_location = locations.find(({x0, y0, x1, y1}) => {
                return (
                    clicked_point.x >= x0 &&
                    clicked_point.x <= x1 &&
                    clicked_point.y >= y0 &&
                    clicked_point.y <= y1
                );
            });

            if (!clicked_location) return; // Early return if no location clicked.

            (_this.shouldResetState()) && _this.resetState(false); // If user clicked a selectable location, select the location entry as origin/destiny.

            const
                {start_icon, final_icon, start_select, final_select} = _this,
                {occ_preview_start, occ_preview_destiny, occ_preview_distance} = _this.occ_path_indicator,
                {start, end} = _this.state,
                {id, node} = clicked_location,
                location_node = nodes.find(node => node.id === clicked_location.node);

            if (!location_node) {
                show_map_tool && console.warn('Localização não encontrada -> ID do local refere um nó que não existe. ID:', clicked_location.node);
                return;
            }
            if (!start) {
                start_select.value = id;
                occ_preview_start.text = `Origem: ${id}`;
                _this.occ_select_indicator.text = 'Toque para selecionar um lote de destino.';

                _this.initStartLocation(location_node, start_icon);
            } else if (start && !end && start !== node) { // End not selected and is different than start.
                final_select.value = id;
                occ_preview_destiny.text = `Destino: ${id}`;

                _this.initFinalLocation(location_node, final_icon); // If location is at another floor, destination is the starts/escalator, so don't put marker in it.
                _this.shouldDrawPath(null);
                _this.occ_select_indicator.text = `Toque para selecionar um lote de origem.`
            }
        }

        this.map_canvas.addEventListener('click', handleClick);
    };

    /**
     * For each location present in locations json, set an option in the select element.
     * @returns {Promise<Awaited<unknown>[]>}
     */
    loadSelectLocations = async function () {
        const args = Array.prototype.slice.call(arguments);

        /**
         * Set placeholders for start and end selects.
         * @returns {Promise<unknown>}
         */
        const placeholder = () => {
            for (let i = 0; args.length > i; i++) {
                args[i].innerHTML = '';
            }
            return new Promise((resolve) => {
                const option = document.createElement('option');
                option.setAttribute('value', '');
                option.setAttributeNode(document.createAttribute('disabled'));
                option.setAttributeNode(document.createAttribute('selected'));
                option.appendChild(document.createTextNode('Selecione aqui...'));
                for (let i = 0; i < args.length; i++) {
                    args[i].appendChild(option.cloneNode(true));
                }
                resolve(true);
            });
        };

        const priority_order = {
            BGS: 14,
            diamond: 13,
            gold: 12,
            silver: 11,
            bronze: 10,
            copper: 9,
            indies: 8,
            educational: 7,
            shopping: 6,
            BGS_esport: 5,
            b2b: 4,
            bathrooms: 3,
            food_courts: 2,
            sector: 1
        }
        /**
         * Filter locations with property 'not_show_select', unprioritize locations with property 'priority = "low"',
         * set itens with property 'group' to have more priority and set them on same optgroup. For the rest, insert options on selects.
         */
        const promises = () => {
            if (!this.start_select || !this.final_select) {
                console.warn('Missing start and end select HTML elements.');
                return;
            }
            const sortedLocations = locations.sort((a, b) => {
                const
                    priority_a = priority_order[a.group] ?? 0,
                    priority_b = priority_order[b.group] ?? 0;

                if (priority_a !== priority_b) return priority_b - priority_a;

                // Natural / numeric-aware compare → "1,2,10", not "1,10,2"
                return String(a.id).localeCompare(String(b.id), 'pt-BR', {
                    numeric: true,
                    sensitivity: 'base',
                });
            });

            // Cache for created groups per <select>.
            const groups_cache = new WeakMap();

            if (!has_selection) return;
            return sortedLocations.map(async ({id, node, group}) => {
                const option = document.createElement('option');
                option.value = node;
                option.textContent = id;

                for (let i = 0; i < args.length; i++) {
                    const select = args[i];
                    if (group) {
                        // Ensure this select already has the group.
                        if (!groups_cache.has(select)) {
                            groups_cache.set(select, {});
                        }
                        const optgroups = groups_cache.get(select);

                        // Create new optgroup tag.
                        if (!optgroups[group]) {
                            const optgroup = document.createElement('optgroup');
                            // Set label as group name on group label, if there's none, set as object property name.
                            optgroup.label = select_groups[group]?.label || group;
                            optgroup.style.color = select_groups[group]?.color || 'black'; // Set group color.
                            select.appendChild(optgroup);
                            optgroups[group] = optgroup;
                        }

                        optgroups[group].appendChild(option.cloneNode(true));
                    } else {
                        // No group, add directly.
                        select.appendChild(option.cloneNode(true));
                    }
                }
            });
        };


        try {
            return await Promise.all([
                await placeholder(),
                await promises()
            ]);
        } catch (error) {
            console.error(error);
        }
    };

    /** Manage sidebar interactions with select elements. */
    handleSelection = () => {
        if (!has_selection || !this.final_select || this.final_select?.tagName === 'select') {
            return;
        }

        const {start_icon, final_icon, start_select, final_select} = this;

        //console.log(final_select, 'fsel');

        // Enable button for opening bootstrap modal.
        function openSearchModal(event) {
            const modal = new bootstrap.Modal(document.getElementById('select_location'));
            modal.show();
        }
        this.search_btn.addEventListener('click', openSearchModal)

        function handleSelect(event) {
            (_this.shouldResetState()) && _this.resetState(false);

            const location_node = nodes.find(({id}) => id === event.target.value);

            if (!location_node || !location_node.x || !location_node.y) { // Location must be valid.
                show_map_tool && console.warn('Localização inválida.');
                return;
            }

            // Set start and end position.
            if (event.target === start_select) {
                _this.initStartLocation(location_node, start_icon);
            } else if (event.target === final_select) {
                if (start_select.value != final_select.value) { // Positions must be different.
                    _this.initFinalLocation(location_node, final_icon);
                } else {
                    // User selected repeated location for final position, then clear it (don't allow setting it).
                    _this.final_select.value = '';
                }
            }

            // Close modal after destiny selection.
            _this.shouldDrawPath(() => {
                const modal = bootstrap.Modal.getInstance(document.getElementById('select_location'));
                modal.hide();
            });
        };

        this.addListener(start_select, 'change', 'change-handle-select-start', handleSelect);
        this.addListener(final_select, 'change', 'change-handle-select-end', handleSelect);
    };

    /** Manage event for clearing path and resetting state when clicking 'clear' button. */
    handleClearPathButton = () => {
        const clearPath = () => {
            const {fixed_start, start_select} = this;
            this.resetState(true);
            // If start location is fixed there is no need to clear select and disable final.
            if (!fixed_start) {
                // Resetting start value; final is already resetted.
                start_select.value = '';
                this.disableFinalSelect();
            }

            this.hideMapLayers();
            this.setQrcodeTemplate();
        }

        this.addListener(document.getElementById('button_clear_path'), 'click', 'click-clear-path', clearPath);
    };

    /** Init values for start and end selects, handle if start select should be visible for fixed_start mode. */
    initModal = () => {
        const {fixed_start, fixed_route, start_select, final_select} = this;

        if (!has_selection) return;

        this.loadSelectLocations(start_select, final_select).then(() => {
            this.emit('process_end', {});
        }).catch((error) => {
            console.error(error);
        });

        // Final select starts disabled if start location is not fixed.        
        // if (!this.final_select) { 
        //     this.disableFinalSelect();
        // }
    };

    /**
     * Activate map layers when subtitle button is clicked, clear current path.
     * Subtitle must have the same sufix for layer and button ocurrences on Animate; e.g.
     * - occ_box.btn_food_court;
     * - occ_box.layer_food_court;
     */
    handleSubtitle = () => {
        function switchVisibility(event) {
            const this_layer = this.layer;

            // Make all layers invisible but this_layer.
            map_layers.forEach(layer => {
                if (this_layer !== layer) {
                    _this.occ_current[`layer_${layer}`].visible = false;
                }
            });

            const current_layer = _this.occ_current[`layer_${this_layer}`];
            current_layer.visible = !current_layer.visible;

            // If layer is visible, set it at qrcode, else, set qrcode template.
            (current_layer.visible) ? _this.makePathQRCode(this_layer) : _this.setQrcodeTemplate();

            _this.state.map_layer_active = current_layer.visible;
            _this.resetState(false);
        }

        map_layers.length && map_layers.forEach(layer => {
            const current_btn = _this.occ_current[`btn_${layer}`];

            if (current_btn) {
                current_btn.removeEventListener('click', switchVisibility);
                current_btn.addEventListener('click', switchVisibility.bind({layer}));
            }
        });
    };

    /**
     * Set the start and final nodes of a map layer from the URL params. This function disables other map interactions, intended for mobile version of the map.
     * Only a map layer or a path can be shown at a time; if URL params for both are present, the path will be prioritized.
     */
    setFixedRoute = () => {
        const {start, end, layer} = this.getUrlParams();

        if (!should_be_fixed_route) return;
        if (!start && !end && !layer) return; // Can't set positions nor set layer without working url param.

        this.fixed_route = true;

        // Draw path if there's valid start and end params.
        if (start && end) {
            const
                start_node = nodes.find(node => node.id === start),
                end_node = nodes.find(node => node.id === end);

            if (start_node && end_node) {
                const {start_icon, final_icon} = this;
                this.initStartLocation(start_node, start_icon);
                this.initFinalLocation(end_node, final_icon);
                this.drawPath(nodes, start_node.id, end_node.id);
            }
            return;
        }
        // Draw layer if param for layer is valid and path params were not given
        if (layer) {
            const current_layer = this?.occ_current[`layer_${layer}`];
            if (current_layer) {
                current_layer.visible = 'true'
            }
            return;
        }
    };

    /**
     * Returns params from URL search params as an object. e.g.
     * URL http://127.0.0.1?start=n1&end=n2 would return: {start: 'n1', end: 'n2'}
     * @returns {Object} Object with the query params as key-value pairs.
     */
    getUrlParams = () => {
        const url_params = new URLSearchParams(window.location.search);
        return Object.fromEntries(url_params.entries());
    };

    /**
     * If map param 'fixed_start' is true, URL search param node was given and is valid,
     * set it as the starting node position and don't allow it to change.
     */
    setFixedStartPoint = () => {
        if (!should_be_fixed_start) return;

        const
            {start} = this.getUrlParams(),
            label_origin = document.querySelector('#label_origin'),
            node_value = nodes.find(({id}) => id === start);

        // Node must be a valid existing node.
        if (!node_value) return;

        const {start_icon, start_select} = this;

        this.initStartLocation(node_value, start_icon);
        this.fixed_start = true;
        // Make start select invisible since the start position is static and only final changes.
        label_origin.style.display = 'none';
        start_select.style.display = 'none';
    };
};

StageMapSelector.propTypes = {
    should_be_fixed_route: PropTypes.bool,
    should_be_fixed_start: PropTypes.bool,
    final_marker_icon_direction: PropTypes.string,
    qrcode_origin_url: PropTypes.string,
    qrcode_size: PropTypes.number,
    qrcode_top: PropTypes.string,
    qrcode_right: PropTypes.string,
    dash_segments: PropTypes.array,
    line_color: PropTypes.string,
    line_thickness: PropTypes.number,
    select_groups: PropTypes.object,
};

StageMapSelector.defaultProps = map.map;

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageMapSelector, {className: 'StageMapSelector'});